import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DiscoveredProduct {
  url: string;
  name: string;
  price: number | null;
  image_url: string | null;
}

export interface ImportResult {
  url: string;
  status: "pending" | "importing" | "success" | "error";
  productName?: string;
  error?: string;
}

export type ImportPhase = "idle" | "importing" | "embedding" | "done";

interface EmbeddingProgress {
  total: number;
  done: number;
  failed: number;
  pending: number;
  processing: number;
  percent: number;
}

interface ImportState {
  isImporting: boolean;
  isMinimized: boolean;
  isDialogOpen: boolean;
  currentIndex: number;
  totalProducts: number;
  successCount: number;
  errorCount: number;
  siteName: string;
  results: ImportResult[];
  phase: ImportPhase;
  embedding: EmbeddingProgress;
}

interface ImportContextType extends ImportState {
  startImport: (products: DiscoveredProduct[], workspaceId: string, siteName: string, onComplete: () => void) => void;
  minimize: () => void;
  maximize: () => void;
  closeDialog: () => void;
  cancelImport: () => void;
}

const ImportContext = createContext<ImportContextType | null>(null);

const initialEmbedding: EmbeddingProgress = {
  total: 0,
  done: 0,
  failed: 0,
  pending: 0,
  processing: 0,
  percent: 0,
};

export function ImportProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const [state, setState] = useState<ImportState>({
    isImporting: false,
    isMinimized: false,
    isDialogOpen: false,
    currentIndex: 0,
    totalProducts: 0,
    successCount: 0,
    errorCount: 0,
    siteName: "",
    results: [],
    phase: "idle",
    embedding: initialEmbedding,
  });

  const cancelledRef = useRef(false);
  const onCompleteRef = useRef<(() => void) | null>(null);
  const importedProductIdsRef = useRef<string[]>([]);
  const workspaceIdRef = useRef<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const embeddingTriggeredRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const triggerProcessor = async () => {
    try {
      await supabase.functions.invoke("process-product-embeddings", {
        body: { trigger: "import-context" },
      });
    } catch (e) {
      console.warn("[ImportContext] failed to trigger embedding processor:", e);
    }
  };

  const fetchEmbeddingStatus = async (workspaceId: string, productIds: string[]): Promise<EmbeddingProgress | null> => {
    try {
      const { data, error } = await supabase.functions.invoke("embedding-queue-status", {
        body: { workspace_id: workspaceId, product_ids: productIds },
      });
      if (error) throw error;
      return {
        total: data.total ?? 0,
        done: data.done ?? 0,
        failed: data.failed ?? 0,
        pending: data.pending ?? 0,
        processing: data.processing ?? 0,
        percent: data.percent ?? 0,
      };
    } catch (e) {
      console.warn("[ImportContext] embedding status poll failed:", e);
      return null;
    }
  };

  const startEmbeddingPhase = useCallback(async (workspaceId: string, productIds: string[], successCount: number, errorCount: number) => {
    if (productIds.length === 0) {
      // Nothing to embed — finalize immediately
      setState(prev => ({
        ...prev,
        phase: "done",
        isImporting: false,
        embedding: { ...initialEmbedding, total: 0, percent: 100 },
      }));
      toast({
        title: "Importação concluída",
        description: `${successCount} produto(s) importado(s)${errorCount > 0 ? `, ${errorCount} erro(s)` : ""}.`,
        variant: errorCount > 0 && successCount === 0 ? "destructive" : "default",
      });
      if (successCount > 0) onCompleteRef.current?.();
      return;
    }

    setState(prev => ({
      ...prev,
      phase: "embedding",
      embedding: { ...initialEmbedding, total: productIds.length, pending: productIds.length },
    }));

    // Kick off processor once (waitUntil on server keeps it alive)
    await triggerProcessor();

    const poll = async () => {
      if (cancelledRef.current) {
        stopPolling();
        return;
      }
      const workspaceId = workspaceIdRef.current;
      if (!workspaceId) return;

      const progress = await fetchEmbeddingStatus(workspaceId, importedProductIdsRef.current);
      if (!progress) return;

      setState(prev => ({ ...prev, embedding: progress }));

      // If queue is empty (total === 0) all items already done. Or done+failed === total.
      const finished = progress.done + progress.failed;
      const allDone = progress.total === 0 || finished >= productIds.length;

      if (allDone) {
        stopPolling();
        setState(prev => ({
          ...prev,
          phase: "done",
          isImporting: false,
          embedding: { ...progress, percent: 100 },
        }));
        toast({
          title: "Importação concluída",
          description: `${successCount} produto(s) importado(s) e indexado(s)${errorCount > 0 ? `, ${errorCount} erro(s)` : ""}${progress.failed > 0 ? `. ${progress.failed} sem embedding.` : ""}.`,
          variant: errorCount > 0 && successCount === 0 ? "destructive" : "default",
        });
        if (successCount > 0) onCompleteRef.current?.();
        return;
      }

      // If nothing has progressed in a while, re-trigger processor (defensive)
      if (progress.pending > 0 && progress.processing === 0) {
        triggerProcessor();
      }
    };

    // Initial poll then interval
    await poll();
    pollTimerRef.current = window.setInterval(poll, 3000);
  }, [stopPolling, toast]);

  const insertSingleProduct = async (
    workspaceId: string,
    product: any,
  ): Promise<{ success: boolean; name?: string; productId?: string; error?: string }> => {
    try {
      const syncMetadata = product.additional_attributes?.length
        ? { attributes: product.additional_attributes }
        : null;

      const { data: savedProduct, error: insertError } = await supabase
        .from("products")
        .insert({
          workspace_id: workspaceId,
          name: product.name,
          description: product.description || null,
          price: product.price ?? null,
          sku: product.sku || null,
          colors: product.colors?.length ? product.colors : null,
          dimensions: product.dimensions || null,
          product_url: product.product_url || null,
          image_url: product.image_url || null,
          type: "product",
          source: "web_scrape",
          is_active: true,
          sync_metadata: syncMetadata as any,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      try {
        await supabase.functions.invoke("enqueue-product-embedding", {
          body: { productId: savedProduct.id },
        });
      } catch (embeddingError) {
        console.warn("Warning: Failed to enqueue embedding:", embeddingError);
      }

      return { success: true, name: product.name, productId: savedProduct.id };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro ao salvar",
      };
    }
  };

  // Returns one OR many import results (multi when scrape-product returns a product list,
  // e.g. for SPA platforms like Prefiro Delivery that expose all items in a single bundle).
  const importProduct = async (
    url: string,
    workspaceId: string
  ): Promise<{ multi: boolean; results: Array<{ success: boolean; name?: string; error?: string; productId?: string }> }> => {
    try {
      const { data, error } = await supabase.functions.invoke("scrape-product", {
        body: { url, workspaceId },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Multi-product response (e.g. Prefiro Delivery)
      if (Array.isArray(data.products) && data.products.length > 0) {
        const results: Array<{ success: boolean; name?: string; error?: string; productId?: string }> = [];
        for (const p of data.products) {
          if (cancelledRef.current) break;
          results.push(await insertSingleProduct(workspaceId, p));
        }
        return { multi: true, results };
      }

      // Single-product response
      if (data.product) {
        return { multi: false, results: [await insertSingleProduct(workspaceId, data.product)] };
      }

      return { multi: false, results: [{ success: false, error: "Produto não encontrado" }] };
    } catch (error) {
      return {
        multi: false,
        results: [{ success: false, error: error instanceof Error ? error.message : "Erro desconhecido" }],
      };
    }
  };

  const startImport = useCallback(async (
    products: DiscoveredProduct[], 
    workspaceId: string, 
    siteName: string,
    onComplete: () => void
  ) => {
    cancelledRef.current = false;
    onCompleteRef.current = onComplete;
    importedProductIdsRef.current = [];
    workspaceIdRef.current = workspaceId;
    embeddingTriggeredRef.current = false;
    stopPolling();

    const initialResults: ImportResult[] = products.map(p => ({
      url: p.url,
      status: "pending",
      productName: p.name
    }));

    setState({
      isImporting: true,
      isMinimized: false,
      isDialogOpen: true,
      currentIndex: 0,
      totalProducts: products.length,
      successCount: 0,
      errorCount: 0,
      siteName,
      results: initialResults,
      phase: "importing",
      embedding: initialEmbedding,
    });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < products.length; i++) {
      if (cancelledRef.current) break;

      const product = products[i];

      setState(prev => ({
        ...prev,
        currentIndex: i,
        results: prev.results.map((r, idx) => 
          idx === i ? { ...r, status: "importing" } : r
        ),
      }));

      const importResult = await importProduct(product.url, workspaceId);

      if (cancelledRef.current) break;

      // Aggregate counts across all sub-results (multi-product responses can produce many)
      const subResults = importResult.results;
      const subSuccess = subResults.filter(r => r.success).length;
      const subError = subResults.length - subSuccess;
      successCount += subSuccess;
      errorCount += subError;
      for (const r of subResults) {
        if (r.success && r.productId) importedProductIdsRef.current.push(r.productId);
      }

      // Use the first result for the row's status; if multi, append a summary in the productName.
      const primary = subResults[0] ?? { success: false, error: "Sem resposta" };
      const displayName = importResult.multi
        ? `${product.name} (+${subResults.length - 1} produtos)`
        : (primary.name || product.name);

      setState(prev => ({
        ...prev,
        successCount,
        errorCount,
        results: prev.results.map((r, idx) =>
          idx === i ? {
            ...r,
            status: primary.success ? "success" : "error",
            productName: displayName,
            error: primary.error,
          } : r
        ),
      }));

      // Trigger processor early to start draining in parallel
      const shouldTrigger =
        subSuccess > 0 &&
        (!embeddingTriggeredRef.current || importedProductIdsRef.current.length % 100 === 0);
      if (shouldTrigger) {
        embeddingTriggeredRef.current = true;
        triggerProcessor();
      }

      if (i < products.length - 1 && !cancelledRef.current) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    if (cancelledRef.current) return;

    // Phase 2: wait for embeddings to complete
    await startEmbeddingPhase(workspaceId, [...importedProductIdsRef.current], successCount, errorCount);
  }, [startEmbeddingPhase, stopPolling]);

  const minimize = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: true, isDialogOpen: false }));
  }, []);

  const maximize = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: false, isDialogOpen: true }));
  }, []);

  const closeDialog = useCallback(() => {
    setState(prev => {
      // If still working (importing or embedding), just minimize
      if (prev.phase === "importing" || prev.phase === "embedding") {
        return { ...prev, isMinimized: true, isDialogOpen: false };
      }
      stopPolling();
      return {
        isImporting: false,
        isMinimized: false,
        isDialogOpen: false,
        currentIndex: 0,
        totalProducts: 0,
        successCount: 0,
        errorCount: 0,
        siteName: "",
        results: [],
        phase: "idle",
        embedding: initialEmbedding,
      };
    });
  }, [stopPolling]);

  const cancelImport = useCallback(() => {
    cancelledRef.current = true;
    stopPolling();
    setState({
      isImporting: false,
      isMinimized: false,
      isDialogOpen: false,
      currentIndex: 0,
      totalProducts: 0,
      successCount: 0,
      errorCount: 0,
      siteName: "",
      results: [],
      phase: "idle",
      embedding: initialEmbedding,
    });
    toast({
      title: "Importação cancelada",
      description: "A importação foi interrompida.",
    });
  }, [stopPolling, toast]);

  return (
    <ImportContext.Provider value={{
      ...state,
      startImport,
      minimize,
      maximize,
      closeDialog,
      cancelImport,
    }}>
      {children}
    </ImportContext.Provider>
  );
}

export function useImport() {
  const context = useContext(ImportContext);
  if (!context) {
    throw new Error("useImport must be used within an ImportProvider");
  }
  return context;
}
